import capitalize from 'lodash/capitalize';
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import Button from '~/components/buttons/NewButton';
import CounterpartChildrenAssociationModal from '~/components/counterparts/CounterpartChildrenAssociationModal';
import NewCounterpartModal from '~/components/counterparts/NewCounterpartModal';
import ComboBox, { BaseComboBoxProps, ComboBoxOption } from '~/components/inputs/Select/ComboBox';
import { HStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useAnalytics } from '~/hooks/useAnalytics';
import IconInstitution from '~/icons/icon-building.svg?react';
import IconPlus from '~/icons/icon-plus.svg?react';
import IconStore from '~/icons/icon-store.svg?react';
import IconBusinessman from '~/icons/icon-user-business.svg?react';
import IconContractor from '~/icons/icon-user-contractor.svg?react';
import { useModal } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useCounterpart, useCounterpartsSearch } from '~/services/counterparts';
import { IdentityType } from '~/types/company-identity';
import { Counterpart } from '~/types/counterparts';
import { isDefined } from '~/types/typeGuards';
import styles from './CounterpartComboBox.module.scss';

interface CounterpartComboBoxProps extends BaseComboBoxProps {
  counterpartType?: 'client' | 'supplier' | 'generic';
  onChange: (value: Counterpart | null) => void;
  value: string | null;
  shortMode?: boolean;
  className?: string;
  hasCounterpartIcon?: boolean;
  hasClearButton?: boolean;
  hasCreateButton?: boolean;
  hasMergeCallToAction?: boolean;
}

const useCounterpartDetails = ({ shortMode }: { shortMode: boolean }) => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'summary_row' });

  return useCallback(
    (counterpart: Counterpart) => {
      const extendedDetails = [
        counterpart.taxNumber ? t('tax_number_label', { value: counterpart.taxNumber }) : '',
        counterpart.vatNumber ? t('vat_number_label', { value: counterpart.vatNumber }) : '',
        isDefined(counterpart.destinationCode) && counterpart.destinationCode !== '0000000'
          ? t('recipient_code_label', { value: counterpart.destinationCode })
          : '',
      ];

      const baseDetails = [
        capitalize(counterpart.address ?? ''),
        `${capitalize(counterpart.city ?? '')}${counterpart.provinceCode ? ` (${counterpart.provinceCode})` : ''}`,
      ];

      const details = shortMode ? baseDetails : [...baseDetails, ...extendedDetails];

      return details.filter(Boolean).join(' • ');
    },
    [t, shortMode],
  );
};

const counterpartToIcon = (counterpart: Counterpart | null) => {
  if (counterpart === null) {
    return null;
  }
  switch (counterpart.identityType) {
    case IdentityType.Pa: {
      return <IconInstitution className={styles.icon} />;
    }
    case IdentityType.Person: {
      return <IconBusinessman className={styles.icon} />;
    }
    case IdentityType.Contractor: {
      return <IconContractor className={styles.icon} />;
    }
    default: {
      return <IconStore className={styles.icon} />;
    }
  }
};

const useCounterpartToOption = ({
  shortMode,
  hasCounterpartIcon,
}: {
  shortMode: boolean;
  hasCounterpartIcon: boolean;
}) => {
  const counterpartDetails = useCounterpartDetails({ shortMode });
  return useCallback(
    (counterpart: Counterpart) => ({
      id: counterpart.id,
      label: counterpart.companyName ?? '',
      media: hasCounterpartIcon ? (
        <div className={styles.iconContainer}>{counterpartToIcon(counterpart)}</div>
      ) : undefined,
      value: counterpart,
      description: counterpartDetails(counterpart),
    }),
    [counterpartDetails, hasCounterpartIcon],
  );
};

interface CounterpartComboBoxFooterProps {
  counterpartType?: 'client' | 'supplier' | 'generic';
  searchText: string;
  hasCreateButton: boolean;
  hasMergeCallToAction: boolean;
  onChange: (counterpart: Counterpart) => void;
}

export const CounterpartComboBoxFooter: React.FC<CounterpartComboBoxFooterProps> = ({
  counterpartType = 'generic',
  hasCreateButton,
  hasMergeCallToAction,
  onChange,
  searchText,
}) => {
  const { t } = useTranslation('inputs', { keyPrefix: 'counterpart_picker' });
  const { showModal } = useModal();
  const { track } = useAnalytics();

  const handleAddClient = async () => {
    track(AnalyticsEvents.COUNTERPART_CREATION_STARTED, { context: 'document-builder' });
    const result = await showModal({ component: NewCounterpartModal, props: {} });

    if (result.action === 'DONE') {
      onChange(result.counterpart);
    }
  };

  const handleResolveDuplication = () => {
    return showModal({
      component: CounterpartChildrenAssociationModal,
      props: { searchText, disableSearch: true },
    });
  };

  return (
    <HStack justify={hasCreateButton ? 'space-between' : 'center'} align="center" className={styles.additionalContent}>
      {hasCreateButton && (
        <Button Icon={IconPlus} onPressStart={handleAddClient} variant="outlined" size="medium">
          {t(`${counterpartType}.add_new`)}
        </Button>
      )}
      {hasMergeCallToAction && (
        <HStack align="center" gap={1}>
          <Text variant="body2" weight="regular">
            {t('duplicated_results')}
          </Text>
          <Button onPressStart={handleResolveDuplication} variant="link">
            {t('associate_them')}
          </Button>
        </HStack>
      )}
    </HStack>
  );
};

const CounterpartComboBox = forwardRef<HTMLDivElement, CounterpartComboBoxProps>((props, ref) => {
  const {
    value,
    shortMode,
    onChange,
    counterpartType,
    hasClearButton,
    className,
    placeholder,
    hasCreateButton = true,
    hasMergeCallToAction = true,
    hasCounterpartIcon = true,
  } = props;
  const { data: selectedCounterpart } = useCounterpart(value);
  const [searchText, setSearchText] = useState<string | null>(selectedCounterpart?.companyName ?? null);

  const [debouncedSearchText] = useDebounce(searchText ?? '', 300);
  const { data } = useCounterpartsSearch(debouncedSearchText.length > 1 ? debouncedSearchText : '');
  const options = useMemo(() => data?.pages.at(0)?.counterparts ?? [], [data]);
  const counterpartToOption = useCounterpartToOption({ shortMode: shortMode || false, hasCounterpartIcon });

  useEffect(() => {
    if (selectedCounterpart !== undefined) {
      setSearchText(selectedCounterpart?.companyName ?? '');
    }
  }, [selectedCounterpart]);

  const optionsCalculated: ComboBoxOption<Counterpart>[] = useMemo(() => {
    return options.map(counterpartToOption);
  }, [counterpartToOption, options]);

  const optionsIncludingCounterpart = useMemo(() => {
    if (!selectedCounterpart || optionsCalculated.some((o) => o.id === selectedCounterpart.id)) {
      return optionsCalculated;
    } else {
      return [counterpartToOption(selectedCounterpart), ...optionsCalculated];
    }
  }, [counterpartToOption, optionsCalculated, selectedCounterpart]);

  return (
    <ComboBox
      {...props}
      allowsEmptyCollection
      className={className}
      additionalContentSticky={hasCreateButton || hasMergeCallToAction}
      additionalContent={
        hasCreateButton || hasMergeCallToAction ? (
          <CounterpartComboBoxFooter
            counterpartType={counterpartType}
            searchText={searchText ?? ''}
            hasCreateButton={hasCreateButton}
            hasMergeCallToAction={hasMergeCallToAction}
            onChange={onChange}
          />
        ) : undefined
      }
      placeholder={placeholder}
      inputValue={searchText ?? ''}
      onChange={(counterpart) => {
        setSearchText(counterpart?.companyName ?? '');
        onChange(counterpart);
      }}
      onInputChange={setSearchText}
      options={optionsIncludingCounterpart}
      ref={ref}
      shouldFlip={false}
      valueToId={(c) => c?.id ?? ''}
      value={selectedCounterpart ?? null}
      hasClearButton={hasClearButton}
    />
  );
});

export default CounterpartComboBox;
